import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Clock, 
  Mail, 
  ArrowRight,
  Loader2,
  Home,
  Megaphone
} from "lucide-react";

export default function AdvertiseSuccess() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(true);

  const adType = searchParams.get("ad_type");

  return (
    <>
      <Helmet>
        <title>{isRTL ? "تم تقديم الإعلان | منصة الفعاليات" : "Ad Submitted | Events Platform"}</title>
      </Helmet>

      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-lg mx-auto text-center"
            >
              {isProcessing ? (
                <Card>
                  <CardContent className="py-16">
                    <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
                    <h1 className="text-2xl font-bold mb-4">
                      {isRTL ? "جاري معالجة الدفع..." : "Processing Payment..."}
                    </h1>
                    <p className="text-muted-foreground">
                      {isRTL ? "يرجى الانتظار" : "Please wait"}
                    </p>
                  </CardContent>
                </Card>
              ) : isSuccess ? (
                <Card>
                  <CardContent className="py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-10 h-10 text-green-600" />
                    </div>
                    
                    <h1 className="text-3xl font-bold mb-4 text-green-600">
                      {isRTL ? "تم الدفع بنجاح!" : "Payment Successful!"}
                    </h1>
                    
                    <p className="text-lg text-muted-foreground mb-8">
                      {isRTL 
                        ? "تم استلام إعلانك وسيتم مراجعته من قبل فريقنا"
                        : "Your ad has been received and will be reviewed by our team"
                      }
                    </p>

                    <div className="space-y-4 text-left bg-muted/50 rounded-lg p-6 mb-8">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <h3 className="font-semibold">
                            {isRTL ? "وقت المراجعة" : "Review Time"}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {isRTL ? "24-48 ساعة عمل" : "24-48 business hours"}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <h3 className="font-semibold">
                            {isRTL ? "إشعار البريد" : "Email Notification"}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {isRTL 
                              ? "ستصلك رسالة عند الموافقة على إعلانك"
                              : "You'll receive an email when your ad is approved"
                            }
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Megaphone className="w-5 h-5 text-primary mt-0.5" />
                        <div>
                          <h3 className="font-semibold">
                            {isRTL ? "نوع الإعلان" : "Ad Type"}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {adType === 'banner' 
                              ? (isRTL ? "إعلان بانر - 7 أيام" : "Banner Ad - 7 days")
                              : (isRTL ? "إعلان جانبي - 7 أيام" : "Sidebar Ad - 7 days")
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link to="/">
                        <Button variant="outline" className="w-full sm:w-auto">
                          <Home className="w-4 h-4 mr-2" />
                          {isRTL ? "الرئيسية" : "Home"}
                        </Button>
                      </Link>
                      <Link to="/advertise">
                        <Button className="w-full sm:w-auto">
                          {isRTL ? "إعلان آخر" : "Another Ad"}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-16">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-4xl">❌</span>
                    </div>
                    
                    <h1 className="text-2xl font-bold mb-4">
                      {isRTL ? "حدث خطأ" : "Something went wrong"}
                    </h1>
                    
                    <p className="text-muted-foreground mb-8">
                      {isRTL 
                        ? "لم نتمكن من معالجة الدفع. يرجى المحاولة مرة أخرى."
                        : "We couldn't process your payment. Please try again."
                      }
                    </p>

                    <Link to="/advertise">
                      <Button>
                        {isRTL ? "المحاولة مرة أخرى" : "Try Again"}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
